<template>
  <div class="root-box">
    <el-table :data="list" :default-expand-all="false" :max-height="tableHeight" :show-header="false" class="no-hover"
      ref="table" stripe :row-class-name="tripRowClassName" :row-key="tripRowKey">
      <el-table-column type="expand">
        <template slot-scope="props">
          <div class="table-expand">
            <el-table :data="props.row.orderList" :show-header="false" class="no-hover"
              :row-class-name="orderRowClassName">
              <el-table-column width="80" class-name="order-index-col">
                <template slot-scope="scope">
                  <div class="order-index-hit" :title="scope.row.id ? '点击复制订单号' : ''"
                    @click="copyOrderId(scope.row.id)">{{ scope.$index + 1 }}</div>
                </template>
              </el-table-column>
              <el-table-column prop="showTime" width="120"></el-table-column>
              <el-table-column prop="orderSource" width="80">
                <template slot-scope="scope">
                  <div v-order-source="scope.row.orderSource"></div>
                </template>
              </el-table-column>
              <el-table-column prop="number" width="60">
                <template slot-scope="scope">{{ scope.row | orderTypeTransfer }}</template>
              </el-table-column>
              <el-table-column prop="price" width="100">
                <template slot-scope="scope">{{ scope.row.price | moneyFormat }}元</template>
              </el-table-column>
              <el-table-column prop="telephone" width="130">
                <template slot-scope="scope">
                  <tel-cell :order="scope.row" />
                </template>
              </el-table-column>
              <el-table-column prop="departure" label="起点">
                <template slot-scope="scope">
                  <span class="pointer" @click="showPassengerMap(scope.row, true)">{{
                    scope.row.departure
                  }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="destination" label="终点">
                <template slot-scope="scope">
                  <span class="pointer" @click="showPassengerMap(scope.row, false)">{{
                    scope.row.destination
                  }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="orderStatus" width="100">
                <template slot-scope="scope">
                  <span class="red" v-if="scope.row.orderStatus === 'NOT_CONFIRMED'">未确认</span>
                  <span class="red" v-else-if="scope.row.orderStatus === 'WAITING'">等待接单</span>
                  <span class="red" v-else-if="scope.row.orderStatus === 'CANCEL'">已取消</span>
                  <span class="red" v-else-if="scope.row.orderStatus === 'ACTIVE'">未分派</span>
                  <span v-if="isReconciled(scope.row)" class="reconcile-tag" title="求解未挂上，初始化后补挂">补挂</span>
                </template>
              </el-table-column>
              <el-table-column width="100" prop="createTime"></el-table-column>
              <el-table-column width="120" label="操作">
                <template slot-scope="scope">
                  <el-button type="text" size="small" :disabled="!canManualPlan(scope.row)"
                    @click="$emit('revoke', scope.row)">撤回</el-button>
                  <el-button type="text" size="small" :disabled="!canManualPlan(scope.row)"
                    @click="$emit('reassign', scope.row)">改派</el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="司机编号" width="140">
        <template slot-scope="scope">
          <span class="route-info pointer" @click="toggleRow(scope.row)">{{ scope.row.driverNo }}</span>
          <span v-if="scope.row.changedAfterSolver">（T2）</span>
          <span v-if="scope.row.isValidTrip === false" class="route-tag-invalid">（异常）</span>
        </template>
      </el-table-column>
      <el-table-column label="行程信息">
        <template slot-scope="scope">
          <div class="route-info pointer" @click="toggleRow(scope.row)">
            <span>余座</span>
            <span :class="leftSeatsClass(scope.row)">{{ scope.row.leftSeats }}</span>
            <span v-if="leftSeatsMismatch(scope.row)" class="route-tag-invalid" title="DB余座与占座反算不一致">
              （占座{{ expectedLeft(scope.row) }}）
            </span>
            <span>{{ scope.row.date }} / {{ scope.row.showTime }}</span>
            <span>{{ scope.row.pathId | pathNameTransfer }}</span>
            <span v-if="scope.row.seats !== 3 && !scope.row.locked && !scope.row.actualTrip" class="muted">
              {{ scope.row.seats }}座
            </span>
            <span :class="scope.row.actualTrip ? 'route-tag-actual' : 'route-tag-planned'">
              （{{ scope.row.actualTrip ? '已有行程' : '规划行程' }}）最近到达：{{ scope.row.lastArriveTime | dateFormat('MM-dd hh:mm')
              }}
            </span>
            <span class="arrive-time">
              预计到达：{{ scope.row.arriveTime | dateFormat('MM-dd hh:mm') }}
            </span>
            <span v-if="invalidReasonText(scope.row)" class="route-tag-invalid">
              {{ invalidReasonText(scope.row) }}
            </span>
          </div>
        </template>
      </el-table-column>
    </el-table>
    <div class="page">
      <span class="pagination-sum">共{{ list.length }}个行程， {{ totalPerson }}人</span>
    </div>
  </div>
</template>

<script>
import TelCell from "@/components/TelCell";
import { Toolkit } from "@/util";
import { SHOW_PASSENGER_MAP } from "@/core/const/global-event";
import { matchTelKeyword, normalizeTelKeyword } from "../../utils/simulate";

export default {
  components: { TelCell },
  props: {
    trips: {
      type: Array,
      default: () => []
    },
    tableHeight: {
      type: Number,
      default: 480
    },
    highlightTel: {
      type: String,
      default: ""
    },
    reconciledOrderIds: {
      type: Array,
      default: () => []
    },
    highlightOrderId: {
      type: [Number, String],
      default: null
    }
  },
  computed: {
    list() {
      return (this.trips || []).map((trip) => {
        return {
          ...trip,
          orderList: trip.orderList || []
        };
      });
    },
    totalPerson() {
      return (this.trips || []).reduce((sum, trip) => sum + (trip.assignedSeats || 0), 0);
    },
    highlightKey() {
      return normalizeTelKeyword(this.highlightTel);
    },
    reconciledIdSet() {
      const set = {};
      (this.reconciledOrderIds || []).forEach((id) => {
        if (id != null) {
          set[String(id)] = true;
        }
      });
      return set;
    }
  },
  methods: {
    tripRowKey(row) {
      return row.id != null
        ? String(row.id)
        : `${row.driverNo || ""}_${row.date || ""}_${row.showTime || ""}_${row.pathId || ""}`;
    },
    tripHasMatch(trip) {
      if (!this.highlightKey) {
        return false;
      }
      return (trip.orderList || []).some((order) => matchTelKeyword(order, this.highlightKey));
    },
    tripRowClassName({ row }) {
      const cls = [];
      if (this.tripHasMatch(row)) {
        cls.push("tel-highlight-row");
      }
      if (row.isValidTrip === false) {
        cls.push("invalid-trip-row");
      }
      if (this.tripHasHighlight(row)) {
        cls.push("slack-focus-row");
      }
      return cls.join(" ");
    },
    orderRowClassName({ row }) {
      const cls = [];
      if (matchTelKeyword(row, this.highlightKey)) {
        cls.push("tel-highlight-row");
      }
      if (this.isReconciled(row)) {
        cls.push("reconciled-order-row");
      }
      if (this.isHighlight(row)) {
        cls.push("slack-focus-row");
      }
      return cls.join(" ");
    },
    isHighlight(order) {
      return !!(order && this.highlightOrderId != null && String(order.id) === String(this.highlightOrderId));
    },
    tripHasHighlight(trip) {
      return (trip.orderList || []).some((order) => this.isHighlight(order));
    },
    focusOrder(orderId) {
      const trip = (this.list || []).find((item) => this.tripHasOrder(item, orderId));
      if (!trip || !this.$refs.table) {
        return;
      }
      this.$refs.table.toggleRowExpansion(trip, true);
      this.$nextTick(() => {
        const body = this.$refs.table.bodyWrapper;
        if (!body) {
          return;
        }
        const row = body.querySelector(".el-table__expanded-cell tr.slack-focus-row")
          || body.querySelector("tr.slack-focus-row");
        if (row && row.scrollIntoView) {
          row.scrollIntoView({ block: "center" });
        }
      });
    },
    tripHasOrder(trip, orderId) {
      return (trip.orderList || []).some((order) => order && String(order.id) === String(orderId));
    },
    isReconciled(order) {
      return !!(order && order.id != null && this.reconciledIdSet[String(order.id)]);
    },
    toggleRow(row) {
      this.$refs.table.toggleRowExpansion(row);
    },
    showPassengerMap(item, isStart) {
      this.$bus.$emit(SHOW_PASSENGER_MAP, {
        type: 1,
        order: item,
        isStart
      });
    },
    copyOrderId(orderId) {
      if (!orderId) {
        return;
      }
      Toolkit.copyContentH5(String(orderId));
      this.$message.success("订单号已复制");
    },
    canManualPlan(order) {
      if (!order || !order.id) {
        return false;
      }
      const status = order.orderStatus;
      return !status || status === "ACTIVE" || status === "NOT_CONFIRMED";
    },
    expectedLeft(trip) {
      const seats = trip.seats == null ? 0 : trip.seats;
      const assigned = trip.assignedSeats == null ? 0 : trip.assignedSeats;
      return Math.max(0, seats - assigned);
    },
    leftSeatsMismatch(trip) {
      if (trip == null || trip.leftSeats == null) {
        return false;
      }
      return trip.leftSeats !== this.expectedLeft(trip);
    },
    leftSeatsClass(trip) {
      return this.leftSeatsMismatch(trip) ? "route-tag-invalid" : "red";
    },
    invalidReasonText(trip) {
      const reasons = trip && trip.invalidReasons ? trip.invalidReasons.filter(Boolean) : [];
      return reasons.join("；");
    }
  }
};
</script>

<style lang="less" scoped>
.root-box {
  height: 100%;
  overflow: hidden;
}

.page {
  padding-top: 4px;
  line-height: 20px;
  box-sizing: border-box;
  height: 28px;
}

.table-expand {
  padding: 0 0 0 48px;
}

.order-index-hit {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 40px;
  margin: -8px -10px;
  padding: 8px 10px;
  box-sizing: content-box;
  cursor: pointer;
}

.route-info {
  font-size: 14px;

  span {
    margin-right: 10px;
  }
}

.pointer {
  cursor: pointer;
}

.red {
  color: #f56c6c;
}

.muted {
  color: #909399;
  font-size: 12px;
}

.route-tag-planned {
  color: #67c23a;
  font-size: 12px;
}

.route-tag-actual {
  color: #409eff;
  font-size: 12px;
}

.route-tag-empty {
  color: #e6a23c;
  font-size: 12px;
}

.route-tag-invalid {
  color: #f56c6c;
  font-size: 12px;
}

.reconcile-tag {
  margin-left: 4px;
  color: #e6a23c;
  font-size: 12px;
}

.arrive-time {
  color: #e9b84e;
  font-size: 12px;
}

.pagination-sum {
  color: #f56c6c;
  font-size: 12px;
  display: inline-block;
}

/deep/ .slack-focus-row td {
  background: #fde2e2 !important;
}

/deep/ .tel-highlight-row {
  background: #fff7e6 !important;

  td {
    background: #fff7e6 !important;
  }
}

/deep/ .invalid-trip-row {
  background: #fef0f0 !important;

  td {
    background: #fef0f0 !important;
  }
}

/deep/ .el-table--striped .tel-highlight-row.el-table__row--striped td {
  background: #fff3d6 !important;
}
</style>
